'use client';

import { getImageUrl, sendRequest } from '@/lib/api-service';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface Blog {
	id: string;
	title: string; // renamed from name
	thumbnail: string;
	datetime: string;
	short_description: string;
}

export default function Page() {
	const [blogs, setBlogs] = useState<Blog[]>([]);
	const [email, setEmail] = useState('');
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [hasNextPage, setHasNextPage] = useState(true);
	const [headerImage, setHeaderImage] = useState<string|null>(null);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const data = await sendRequest("GET", `/blogs/?page=${page}`);
				setBlogs(data.results);
				setHasNextPage(!!data.next);
			} catch (error) {
				console.error(error);
			}
		};

		const fetchDynamicImages = async () => {
			const response = await sendRequest('POST', '/dynamic-images/', {codes:["know"]}, true);
			if (response && Array.isArray(response)) {
				const knowImg = response.find((img: { code: string }) => img.code === "know");
				if (knowImg && knowImg.image) setHeaderImage(knowImg.image);
			}
		};
		fetchDynamicImages();
		fetchData();
	}, [page]);

	const handleSubmit = async () => {
		if (!email) return;
		setLoading(true);
		const response = await sendRequest("POST", "/newsletter/email/", { email });
		if (response) {
			toast.success("Амжилттай бүртгэгдлээ!");
		}
		setLoading(false);
		setEmail('');
	};

	return (
		<div className="bg-white max-w-[1920px] mx-auto">
			<div className="relative w-full min-h-[50vh]">
				{headerImage && (
					<Image
						src={getImageUrl(headerImage)}
						alt="Who we are"
						fill
						className="object-cover"
					/>
				)}
				<div className="absolute inset-0 bg-black/10 flex items-center justify-center">
					<h1 className="text-4xl md:text-6xl font-bold mb-4 drop-shadow-md header text-transparent bg-clip-text bg-gradient-to-b from-fire-500 to-fire-900 uppercase">
						Мэдсэн үү?
					</h1>
				</div>
				{/* Waves */}
				{[1, 2, 3].map((num) => (
					<div
						key={num}
						className="w-full h-[20vh] opacity-25 bg-no-repeat bg-top bg-cover absolute bottom-0"
						style={{ backgroundImage: `url('/waves/${num}.svg')` }}
					/>
				))}
				<div
					className="w-full h-[30vh] bg-no-repeat bg-top bg-cover absolute bottom-0"
					style={{ backgroundImage: "url('/waves/low.svg')" }}
				/>
			</div>

			<div className="flex items-center justify-center py-6 mt-10">
				<input
					type="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					placeholder="Имэйлээ оруулна уу"
					className="rounded-s-lg px-4 py-4 text-xl border-mountain-600 border-2"
				/>
				<button
					onClick={handleSubmit}
					disabled={loading}
					className="bg-mountain-600 hover:bg-mountain-700 text-white px-4 py-4 rounded-e-lg w-42 border-2 border-mountain-600  text-xl"
				>
					{loading ? (
						<div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
					) : (
						'Нэгдэх'
					)}
				</button>
			</div>

			{/* Blog Grid */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl px-5 mx-auto py-10 gap-6">
				{blogs && blogs.map((blog) => (
					<Link
						href={`/about/know/${blog.id}`}
						key={blog.id}
						className="group rounded-2xl overflow-hidden shadow-xs hover:shadow-xl transition duration-300 border border-gray-200 bg-white"
					>
						<div className="w-full h-48 overflow-hidden">
							<img
								src={getImageUrl(blog.thumbnail)}
								alt={blog.title}
								className="w-full h-full object-cover"
							/>
						</div>
						<div className="p-4">
							<p className="text-xs text-gray-500 mb-1">
								{new Date(blog.datetime).toLocaleDateString()}
							</p>
							<h2 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition line-clamp-1">
								{blog.title}
							</h2>
							<p className="text-sm text-gray-600 line-clamp-2">
								{blog.short_description}
							</p>
						</div>
					</Link>
				))}
			</div>


			{/* Pagination */}
			<div className="flex justify-center items-center gap-4 pb-10">
				<button
					disabled={page === 1}
					onClick={() => setPage((p) => p - 1)}
					className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
				>
					Өмнөх
				</button>
				<span>Хуудас {page}</span>
				<button
					disabled={!hasNextPage}
					onClick={() => setPage((p) => p + 1)}
					className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
				>
					Дараах
				</button>
			</div>
		</div>
	);
}
